<template>
  <div class="report-detail-page">
    <div class="detail-toolbar">
      <div>
        <p class="detail-subtitle">{{ showingText }}</p>
      </div>

      <div class="right-filters">
        <b-form-input
          v-model="search"
          :placeholder="pageConfig.searchPlaceholder"
          @input="handleSearchInput"
        />
        <b-form-select
          v-if="canFilterByCategory"
          v-model="categoryId"
          :options="categoryFilterOptions"
          @change="handleCategoryFilterChange"
        />
        <b-form-select
          v-if="canFilterByCategory"
          v-model="secondaryCategoryId"
          :options="secondaryCategoryFilterOptions"
          :disabled="!categoryId"
          @change="handleCategoryFilterChange"
        />
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          @change="handlePerPageChange"
        />
      </div>
    </div>

    <div
      v-if="isLoading && !rows.length"
      class="state-panel text-center py-3"
    >
      <b-spinner label="Loading report data..." />
    </div>

    <b-alert
      v-else-if="loadError"
      show
      variant="danger"
      class="mb-2"
    >
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <span>{{ loadError }}</span>
        <b-button
          size="sm"
          variant="outline-danger"
          @click="fetchRows"
        >
          Retry
        </b-button>
      </div>
    </b-alert>

    <div
      v-else-if="!rows.length"
      class="state-panel text-center py-3"
    >
      <feather-icon icon="FileTextIcon" size="40" class="mb-1" />
      <p class="mb-0">{{ pageConfig.emptyText }}</p>
    </div>

    <b-card
      v-else
      no-body
      class="detail-card"
    >
      <div v-if="isLoading" class="refresh-state">
        <b-spinner small label="Refreshing report data..." />
        <span>Refreshing...</span>
      </div>

      <div class="table-wrap">
        <table class="report-table">
          <thead>
            <tr>
              <th
                v-for="column in pageConfig.columns"
                :key="column.key"
                class="mypoda-sortable-th"
                @click="toggleSort(column.key)"
              >
                {{ column.label }}
                <span class="sort-icons" :class="{ 'is-active': sortKey === column.key }">
                  <feather-icon icon="ChevronUpIcon" size="12" />
                  <feather-icon icon="ChevronDownIcon" size="12" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="rowKey(row)"
            >
              <td
                v-for="column in pageConfig.columns"
                :key="`${rowKey(row)}-${column.key}`"
              >
                <template v-if="column.type === 'ad'">
                  <div class="cell-media">
                    <div
                      v-if="adCell(row, column.key).image"
                      class="ad-thumb-wrap"
                    >
                      <img
                        class="ad-thumb"
                        :src="adCell(row, column.key).image"
                        alt="Ad"
                      >
                    </div>
                    <div v-else class="ad-thumb ad-thumb--empty">
                      <feather-icon icon="ImageIcon" size="14" />
                    </div>
                    <span>{{ adCell(row, column.key).title || 'Untitled ad' }}</span>
                  </div>
                </template>

                <template v-else-if="column.type === 'company'">
                  <div class="cell-media">
                    <b-avatar
                      :src="companyCell(row, column.key).logo || null"
                      size="32"
                      variant="light-secondary"
                    >
                      {{ initials(companyCell(row, column.key).name, 'C') }}
                    </b-avatar>
                    <span>{{ companyCell(row, column.key).name || '—' }}</span>
                  </div>
                </template>

                <template v-else-if="column.type === 'person'">
                  <div class="cell-media">
                    <b-avatar
                      :src="personCell(row, column.key).profile_image || null"
                      size="32"
                      variant="light-secondary"
                    >
                      {{ initials(personCell(row, column.key).name, 'U') }}
                    </b-avatar>
                    <span>{{ personCell(row, column.key).name || '—' }}</span>
                  </div>
                </template>

                <template v-else-if="column.type === 'preview'">
                  <div class="followers-stack">
                    <b-avatar
                      v-for="(preview, index) in previewItems(row, column.key)"
                      :key="`${rowKey(row)}-${column.key}-${index}`"
                      size="26"
                      :src="preview.profile_image || null"
                      variant="light-secondary"
                    >
                      U
                    </b-avatar>
                    <span class="followers-count">
                      {{ previewCount(row, column.key) }}
                    </span>
                  </div>
                </template>

                <template v-else-if="column.type === 'status'">
                  <span class="mypoda-status-tag" :class="statusTagClass(row[column.key])">
                    {{ displayValue(row[column.key]) }}
                  </span>
                </template>

                <template v-else-if="column.type === 'actions'">
                  <div class="actions-cell">
                    <b-button
                      v-if="canUnsuspend"
                      size="sm"
                      variant="flat-primary"
                      @click="openSuspendedModal(row)"
                    >
                      Details
                    </b-button>

                    <b-button
                      v-if="canUnsuspend"
                      size="sm"
                      variant="outline-danger"
                      :disabled="isActionBusy('unsuspend', row.id)"
                      @click="unsuspendRow(row)"
                    >
                      {{ isActionBusy('unsuspend', row.id) ? 'Saving...' : 'Unsuspend' }}
                    </b-button>

                    <b-button
                      v-if="canDeleteReport"
                      size="sm"
                      variant="flat-danger"
                      :disabled="isActionBusy('delete-report', row.report_id)"
                      @click="deleteReport(row)"
                    >
                      Delete report
                    </b-button>

                    <b-button
                      v-if="canDeleteAd"
                      size="sm"
                      variant="flat-danger"
                      :disabled="isActionBusy('delete-ad', row.id)"
                      @click="deleteAd(row)"
                    >
                      Delete ad
                    </b-button>

                    <b-button
                      v-if="canViewDetail(row)"
                      size="sm"
                      variant="flat-primary"
                      @click="viewDetail(row)"
                    >
                      View
                    </b-button>

                    <b-button
                      v-if="showsAdsManagementShortcut"
                      size="sm"
                      variant="flat-secondary"
                      @click="openAdsManagement()"
                    >
                      Ads Mgmt
                    </b-button>
                  </div>
                </template>

                <template v-else-if="column.key === 'report_id'">
                  #{{ displayValue(row[column.key]) }}
                </template>

                <template v-else>
                  {{ displayValue(row[column.key]) }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer">
        <AdminPaginationBar
          :page="page"
          :per-page="perPage"
          :total="pagination.total"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <suspended-entity-modal
      :visible.sync="suspendedModalOpen"
      :entity-type="suspendedEntityType"
      :title="suspendedModalTitle"
      :message="suspendedModalMessage"
      :entity="suspendedEntity"
      primary-label="Unsuspend"
      @primary="unsuspendFromModal"
    />
  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BFormInput,
  BFormSelect,
  BSpinner,
} from 'bootstrap-vue'
import { adminPanelApi, reportRouteApiMap } from '@/services/adminPanelApi'
import { normalizePagination } from '@/utils/adminApiHelpers'
import { confirmAction, notifyError, notifySuccess } from '@/utils/uiActions'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import SuspendedEntityModal from '@/components/admin/SuspendedEntityModal.vue'

const REPORT_SORT_KEY_MAP = {
  'reports-reported-by-users': {
    report_id: 'id',
    date: 'date',
    reason: 'reason',
  },
  'reports-suspended-users': {
    id: 'id',
    person: 'name',
    suspended_on: 'suspended_on',
  },
  'reports-suspended-advertisers': {
    id: 'id',
    company: 'company_name',
    suspended_on: 'suspended_on',
  },
  'reports-reject-ads': {
    no: 'id',
    ad: 'title',
    reject_date: 'date',
    request_date: 'date',
  },
  'reports-ads': {
    no: 'id',
    title: 'title',
    publish_date: 'publish_date',
    clicks: 'clicks',
    likes: 'likes',
  },
  'reports-advertisers': {
    id: 'id',
    company: 'company_name',
    joined_date: 'joined_date',
    ads_posted: 'ads_posted',
    active_ads: 'active_ads',
  },
  'reports-users': {
    person: 'name',
    status: 'status',
    joined_date: 'joined_date',
  },
}

const REPORT_PAGE_CONFIGS = {
  'reports-reported-by-users': {
    collectionKey: 'reports',
    searchPlaceholder: 'Search by ad or reporter',
    emptyText: 'No reported ads matched your filters.',
    columns: [
      { key: 'report_id', label: 'Report ID' },
      { key: 'ad', label: 'Reported Ad', type: 'ad' },
      { key: 'company', label: 'Company', type: 'company' },
      { key: 'reported_by', label: 'Reported By', type: 'person' },
      { key: 'reason', label: 'Reason' },
      { key: 'date', label: 'Date' },
      { key: 'actions', label: 'Actions', type: 'actions' },
    ],
  },
  'reports-suspended-advertisers': {
    collectionKey: 'advertisers',
    searchPlaceholder: 'Search advertisers',
    emptyText: 'No suspended advertisers matched your filters.',
    columns: [
      { key: 'id', label: 'ID' },
      { key: 'company', label: 'Advertisers', type: 'company' },
      { key: 'suspended_on', label: 'Status Note' },
      { key: 'contact', label: 'Contact' },
      { key: 'actions', label: 'Actions', type: 'actions' },
    ],
  },
  'reports-suspended-users': {
    collectionKey: 'users',
    searchPlaceholder: 'Search users',
    emptyText: 'No suspended users matched your filters.',
    columns: [
      { key: 'id', label: 'ID' },
      { key: 'person', label: 'Users', type: 'person' },
      { key: 'suspended_on', label: 'Status Note' },
      { key: 'contact', label: 'Contact' },
      { key: 'actions', label: 'Actions', type: 'actions' },
    ],
  },
  'reports-users': {
    collectionKey: 'users',
    searchPlaceholder: 'Search users or contact numbers',
    emptyText: 'No users matched your filters.',
    columns: [
      { key: 'no', label: 'No' },
      { key: 'person', label: 'Name', type: 'person' },
      { key: 'following', label: 'Following', type: 'preview' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'joined_date', label: 'Joined' },
      { key: 'contact', label: 'Contact' },
      { key: 'actions', label: 'Actions', type: 'actions' },
    ],
  },
  'reports-reject-ads': {
    collectionKey: 'ads',
    searchPlaceholder: 'Search rejected ads or advertisers',
    emptyText: 'No rejected ads matched your filters.',
    columns: [
      { key: 'no', label: 'No' },
      { key: 'ad', label: 'Rejected Ad', type: 'ad' },
      { key: 'company', label: 'Company', type: 'company' },
      { key: 'reason', label: 'Reason' },
      { key: 'request_date', label: 'Request Date' },
      { key: 'reject_date', label: 'Reject Date' },
      { key: 'actions', label: 'Actions', type: 'actions' },
    ],
  },
  'reports-ads': {
    collectionKey: 'ads',
    searchPlaceholder: 'Search ads or advertisers',
    emptyText: 'No ads matched your filters.',
    columns: [
      { key: 'no', label: 'No' },
      { key: 'ad', label: 'Ads List', type: 'ad' },
      { key: 'company', label: 'Company', type: 'company' },
      { key: 'category', label: 'Primary Category' },
      { key: 'secondary_category', label: 'Secondary Category' },
      { key: 'clicks', label: 'Clicks' },
      { key: 'publish_date', label: 'Publish Date' },
      { key: 'expiry_date', label: 'Expired Date' },
      { key: 'likes', label: 'Like' },
      { key: 'dislikes', label: 'Dislike' },
      { key: 'reports', label: 'Report' },
      { key: 'shares', label: 'Shared' },
      { key: 'actions', label: 'Actions', type: 'actions' },
    ],
  },
  'reports-advertisers': {
    collectionKey: 'advertisers',
    searchPlaceholder: 'Search advertisers',
    emptyText: 'No advertisers matched your filters.',
    columns: [
      { key: 'id', label: 'ID' },
      { key: 'company', label: 'Company Name', type: 'company' },
      { key: 'followers', label: 'Followers', type: 'preview' },
      { key: 'joined_date', label: 'Joined' },
      { key: 'ads_posted', label: 'Ads Posted' },
      { key: 'active_ads', label: 'Active Ads' },
      { key: 'actions', label: 'Actions', type: 'actions' },
    ],
  },
}

const defaultPagination = () => ({
  currentPage: 1,
  perPage: 10,
  total: 0,
  lastPage: 0,
})

export default {
  name: 'ReportDetails',
  components: {
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BFormInput,
    BFormSelect,
    BSpinner,
    SuspendedEntityModal,
    AdminPaginationBar,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      rows: [],
      pagination: defaultPagination(),
      search: '',
      perPage: 10,
      page: 1,
      categoryId: null,
      secondaryCategoryId: null,
      categoryOptions: [],
      perPageOptions: [
        { value: 10, text: '10 per page' },
        { value: 20, text: '20 per page' },
        { value: 50, text: '50 per page' },
      ],
      searchDebounce: null,
      actionLoading: {},
      sortKey: '',
      sortDesc: false,
      suspendedModalOpen: false,
      suspendedEntity: {},
      suspendedEntityType: 'user',
      suspendedModalTitle: 'Suspended Account',
      suspendedModalMessage: 'This account is currently suspended.',
      pendingUnsuspendRow: null,
    }
  },
  computed: {
    pageConfig() {
      return REPORT_PAGE_CONFIGS[this.$route.name] || REPORT_PAGE_CONFIGS['reports-reported-by-users']
    },
    canUnsuspend() {
      return ['reports-suspended-users', 'reports-suspended-advertisers'].includes(this.$route.name)
    },
    canDeleteReport() {
      return this.$route.name === 'reports-reported-by-users'
    },
    canDeleteAd() {
      return this.$route.name === 'reports-ads'
    },
    canFilterByCategory() {
      return this.$route.name === 'reports-ads'
    },
    categoryFilterOptions() {
      const parents = this.categoryOptions.filter(item => !item.parentId)
      return [
        { value: null, text: 'All primary/any category' },
        ...parents,
      ]
    },
    secondaryCategoryFilterOptions() {
      const primaryId = Number(this.categoryId || 0)
      if (!primaryId) {
        return [{ value: null, text: 'Select primary category first' }]
      }
      const children = this.categoryOptions.filter(item => Number(item.parentId) === primaryId)
      if (!children.length) {
        return [{ value: null, text: 'No child categories for this primary' }]
      }
      return [
        { value: null, text: 'All secondary categories' },
        ...children,
      ]
    },
    showsAdsManagementShortcut() {
      return ['reports-reported-by-users', 'reports-reject-ads'].includes(this.$route.name)
    },
    showingText() {
      if (!this.pagination.total) return 'No records yet'

      const start = ((this.page - 1) * this.perPage) + 1
      const end = Math.min(start + this.rows.length - 1, this.pagination.total)
      return `Showing ${start} to ${end} of ${this.pagination.total} entries`
    },
  },
  watch: {
    '$route.name': {
      immediate: true,
      async handler() {
        this.resetPageState()
        if (this.canFilterByCategory) {
          await this.loadCategoryOptions()
        }
        this.fetchRows()
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounce)
  },
  methods: {
    async loadCategoryOptions() {
      try {
        const res = await adminPanelApi.categories()
        const payload = res?.data?.data || res?.data || {}
        const list = payload.categories || (Array.isArray(payload) ? payload : [])
        this.categoryOptions = (Array.isArray(list) ? list : [])
          .filter(item => item && item.is_active !== false)
          .map(item => {
            const parentRaw = item.parent_id != null ? Number(item.parent_id) : null
            return {
              value: Number(item.id),
              text: String(item.name || `Category ${item.id}`),
              parentId: Number.isFinite(parentRaw) && parentRaw > 0 ? parentRaw : null,
            }
          })
          .filter(item => item.value > 0 && item.text)
      } catch (error) {
        this.categoryOptions = []
      }
    },
    handleCategoryFilterChange() {
      const primaryId = Number(this.categoryId || 0)
      const secondaryId = Number(this.secondaryCategoryId || 0)
      const childIds = this.categoryOptions
        .filter(item => Number(item.parentId) === primaryId)
        .map(item => Number(item.value))
      if (!secondaryId || !childIds.includes(secondaryId)) {
        this.secondaryCategoryId = null
      }
      this.page = 1
      this.fetchRows()
    },
    toggleSort(key) {
      const column = this.pageConfig.columns.find(col => col.key === key)
      if (!column || column.type === 'actions') return

      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc
      } else {
        this.sortKey = key
        this.sortDesc = false
      }
      this.page = 1
      this.fetchRows()
    },
    buildSortParam() {
      if (!this.sortKey) return undefined
      const column = this.pageConfig.columns.find(col => col.key === this.sortKey)
      if (!column || column.type === 'actions') return undefined

      const routeMap = REPORT_SORT_KEY_MAP[this.$route.name] || {}
      const apiKey = routeMap[this.sortKey] || this.sortKey
      return `${this.sortDesc ? '-' : ''}${apiKey}`
    },
    openSuspendedModal(row) {
      const isUser = this.$route.name === 'reports-suspended-users'
      this.suspendedEntityType = isUser ? 'user' : 'advertiser'
      this.suspendedModalTitle = isUser ? 'Suspended User' : 'Suspended Advertiser'
      this.suspendedModalMessage = 'Review suspension details before restoring this account.'
      this.suspendedEntity = {
        name: isUser ? this.personCell(row, 'person').name : this.companyCell(row, 'company').name,
        avatar: isUser ? this.personCell(row, 'person').profile_image : this.companyCell(row, 'company').logo,
        contact: row.contact || row.email || '',
        suspendedOn: row.suspended_on || '',
      }
      this.pendingUnsuspendRow = row
      this.suspendedModalOpen = true
    },
    async unsuspendFromModal() {
      if (!this.pendingUnsuspendRow) return
      await this.unsuspendRow(this.pendingUnsuspendRow)
      this.suspendedModalOpen = false
      this.pendingUnsuspendRow = null
    },
    resetPageState() {
      clearTimeout(this.searchDebounce)
      this.rows = []
      this.loadError = ''
      this.search = ''
      this.page = 1
      this.perPage = 10
      this.categoryId = null
      this.secondaryCategoryId = null
      this.pagination = defaultPagination()
      this.actionLoading = {}
      this.sortKey = ''
      this.sortDesc = false
    },
    normalizeRows(payload) {
      const list = payload?.[this.pageConfig.collectionKey]
      if (Array.isArray(list)) return list

      const fallback = Object.values(payload || {}).find(value => Array.isArray(value))
      return Array.isArray(fallback) ? fallback : []
    },
    onPageChange(nextPage) {
      this.page = nextPage
      this.fetchRows()
    },
    async fetchRows() {
      const apiKey = reportRouteApiMap[this.$route.name]
      if (!apiKey || !adminPanelApi[apiKey]) return

      this.isLoading = true
      this.loadError = ''

      try {
        const params = {
          page: this.page,
          per_page: this.perPage,
        }

        if (this.search.trim()) {
          params.search = this.search.trim()
        }

        if (this.canFilterByCategory && this.categoryId) {
          params.category_id = this.categoryId
        }
        if (this.canFilterByCategory && this.secondaryCategoryId) {
          params.secondary_category_id = this.secondaryCategoryId
        }

        const sort = this.buildSortParam()
        if (sort) params.sort = sort

        const response = await adminPanelApi[apiKey](params)
        const payload = response.data?.data || {}

        this.rows = this.normalizeRows(payload)
        const meta = normalizePagination(payload, {}, response)
        this.pagination = {
          currentPage: meta.currentPage,
          perPage: meta.perPage,
          total: meta.total,
          lastPage: meta.lastPage,
        }
        this.page = meta.currentPage
        this.perPage = meta.perPage
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Unable to load report data.'
        this.rows = []
        this.pagination = defaultPagination()
      } finally {
        this.isLoading = false
      }
    },
    handleSearchInput() {
      clearTimeout(this.searchDebounce)
      this.page = 1
      this.searchDebounce = setTimeout(() => {
        this.fetchRows()
      }, 350)
    },
    handlePerPageChange() {
      this.page = 1
      this.fetchRows()
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') return '—'
      return value
    },
    initials(name, fallback = 'U') {
      const parts = String(name || '')
        .split(' ')
        .map(part => part.trim())
        .filter(Boolean)

      if (!parts.length) return fallback

      return parts
        .slice(0, 2)
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
    },
    rowKey(row) {
      return [
        row.report_id,
        row.id,
        row.no,
        row.company_name,
        row.name,
      ].find(value => value !== null && value !== undefined && value !== '') || JSON.stringify(row)
    },
    personCell(row, key) {
      if (key === 'reported_by') return row.reported_by || {}
      if (row[key] && typeof row[key] === 'object') return row[key]

      return {
        name: row.name || '',
        profile_image: row.profile_image || '',
      }
    },
    companyCell(row, key) {
      if (row[key] && typeof row[key] === 'object') return row[key]

      return {
        name: row.company_name || '',
        logo: row.logo || '',
      }
    },
    adCell(row, key) {
      if (row[key] && typeof row[key] === 'object') return row[key]

      return {
        title: row.title || '',
        image: row.image || '',
      }
    },
    previewItems(row, key) {
      const preview = row[key]?.preview
      return Array.isArray(preview) ? preview : []
    },
    previewCount(row, key) {
      const count = Number(row[key]?.count)
      if (!Number.isFinite(count)) return '0'
      return count
    },
    statusVariant(status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized === 'active') return 'light-success'
      if (normalized === 'blocked') return 'light-warning'
      if (normalized === 'suspended') return 'light-danger'
      return 'light-secondary'
    },
    statusTagClass(status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized === 'active') return 'status-active'
      if (normalized === 'suspended' || normalized === 'blocked') return 'status-inactive'
      return 'status-pill--pending'
    },
    actionKey(action, id) {
      return `${action}-${id}`
    },
    isActionBusy(action, id) {
      return Boolean(this.actionLoading[this.actionKey(action, id)])
    },
    setActionBusy(action, id, value) {
      const key = this.actionKey(action, id)
      if (value) {
        this.$set(this.actionLoading, key, true)
      } else {
        this.$delete(this.actionLoading, key)
      }
    },
    canViewDetail(row) {
      if (['reports-suspended-users', 'reports-users'].includes(this.$route.name)) return Boolean(row.id)
      if (['reports-suspended-advertisers', 'reports-advertisers'].includes(this.$route.name)) return Boolean(row.id)
      if (this.$route.name === 'reports-reject-ads') return Boolean(row.id)
      if (this.$route.name === 'reports-reported-by-users') return Boolean(row.reported_by?.id || row.id)
      return false
    },
    viewDetail(row) {
      if (['reports-suspended-users', 'reports-users'].includes(this.$route.name)) {
        this.$router.push({ name: 'client-profile', params: { id: String(row.id) } })
        return
      }

      if (['reports-suspended-advertisers', 'reports-advertisers'].includes(this.$route.name)) {
        this.$router.push({ name: 'advertiser-overall-details', params: { id: String(row.id) } })
        return
      }

      if (this.$route.name === 'reports-reject-ads') {
        this.$router.push({ name: 'ad-detail', params: { id: String(row.id) } })
        return
      }

      if (this.$route.name === 'reports-reported-by-users' && row.reported_by?.id) {
        this.$router.push({ name: 'client-profile', params: { id: String(row.reported_by.id) } })
      }
    },
    openAdsManagement() {
      this.$router.push({ name: 'ads-video-management' })
    },
    async unsuspendRow(row) {
      const id = row?.id
      if (!id || this.isActionBusy('unsuspend', id)) return

      const isUser = this.$route.name === 'reports-suspended-users'
      const label = isUser ? 'user' : 'advertiser'

      await confirmAction(
        this,
        {
          title: `Unsuspend ${label}?`,
          text: `This will restore the ${label} account.`,
          confirmButtonText: 'Unsuspend',
        },
        async () => {
          this.setActionBusy('unsuspend', id, true)

          try {
            if (isUser) {
              await adminPanelApi.unsuspendUser(id)
            } else {
              await adminPanelApi.unsuspendAdvertiser(id)
            }

            notifySuccess(this, `${label.charAt(0).toUpperCase() + label.slice(1)} unsuspended successfully.`)
            await this.refetchAfterMutation()
          } catch (error) {
            notifyError(this, error.response?.data?.message || `Unable to unsuspend the ${label}.`)
          } finally {
            this.setActionBusy('unsuspend', id, false)
          }
        },
      )
    },
    async deleteReport(row) {
      const reportId = row?.report_id
      if (!reportId || this.isActionBusy('delete-report', reportId)) return

      await confirmAction(
        this,
        {
          title: 'Delete report?',
          text: 'This will permanently remove the selected report record.',
          confirmButtonText: 'Delete',
        },
        async () => {
          this.setActionBusy('delete-report', reportId, true)

          try {
            await adminPanelApi.deleteReport(reportId)
            notifySuccess(this, 'Report deleted successfully.')
            await this.refetchAfterMutation()
          } catch (error) {
            notifyError(this, error.response?.data?.message || 'Unable to delete the report.')
          } finally {
            this.setActionBusy('delete-report', reportId, false)
          }
        },
      )
    },
    async deleteAd(row) {
      const id = row?.id
      if (!id || this.isActionBusy('delete-ad', id)) return

      await confirmAction(
        this,
        {
          title: 'Delete ad?',
          text: 'This will permanently remove the selected ad.',
          confirmButtonText: 'Delete',
        },
        async () => {
          this.setActionBusy('delete-ad', id, true)

          try {
            await adminPanelApi.deleteAd(id)
            notifySuccess(this, 'Ad deleted successfully.')
            await this.refetchAfterMutation()
          } catch (error) {
            notifyError(this, error.response?.data?.message || 'Unable to delete the ad.')
          } finally {
            this.setActionBusy('delete-ad', id, false)
          }
        },
      )
    },
    async refetchAfterMutation() {
      if (this.rows.length === 1 && this.page > 1) {
        this.page -= 1
      }

      await this.fetchRows()
    },
  },
}
</script>

<style scoped lang="scss">
.detail-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.detail-subtitle {
  margin: 0;
  color: #6e6b7b;
}

.detail-card {
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.1);
}

.right-filters {
  margin-left: auto;
  display: flex;
  gap: 0.75rem;
  width: 420px;
}

.refresh-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem 0;
  color: #6e6b7b;
  font-size: 0.85rem;
}

.table-wrap {
  overflow-x: auto;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
}

.report-table th,
.report-table td {
  border: 1px solid #ecebf1;
  padding: 0.65rem 0.75rem;
  color: #5f6274;
  font-size: 0.92rem;
  vertical-align: middle;
}

.report-table th {
  background: #fafafe;
  color: #7f8192;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.cell-media {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ad-thumb-wrap {
  width: 42px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.ad-thumb {
  width: 42px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.ad-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f4f7;
  color: #9ca3af;
}

.actions-cell {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.followers-stack {
  display: flex;
  align-items: center;
}

.followers-stack > * {
  margin-right: -8px;
}

.followers-count {
  margin-left: 12px;
  color: #6b7280;
  font-size: 0.85rem;
  white-space: nowrap;
}

.state-panel {
  min-height: 260px;
  border: 1px dashed #d9d6e5;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.75rem;
  color: #6e6b7b;
}

.table-footer {
  padding: 0.85rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

@media (max-width: 991.98px) {
  .detail-toolbar {
    flex-direction: column;
  }

  .right-filters {
    width: 100%;
    margin-left: 0;
    flex-direction: column;
  }
}
</style>
